/**
 * SPA-style navigation.
 *
 * Intercepts same-origin link clicks and GET form submits, fetches the target
 * page in the background, and swaps only <main data-main>. The header, nav,
 * announcement bar, rose row, cart drawer and footer are never re-rendered, so
 * they keep their DOM, their scroll/sticky state and their live cart state
 * across a "page change".
 *
 * How it stays compatible with the rest of the theme:
 *
 *  - theme.js already re-initialises a section on `shopify:section:load` (that
 *    is how the theme editor works). After a swap we fire that event for every
 *    section in the new <main>, which re-runs animations, sliders and the
 *    resize/height bookkeeping. We never touch theme.js itself.
 *  - The ~48 custom elements upgrade themselves when their markup is inserted,
 *    and tear themselves down via disconnectedCallback when the old <main> is
 *    removed, so nothing leaks between pages.
 *  - Per-section <script src> tags are stripped out of the parsed markup and
 *    re-created by hand — scripts that arrive through DOMParser never execute
 *    on their own — and each src is only ever run once per session.
 *  - Per-section stylesheets are hoisted into <head> and awaited *before* the
 *    swap, so new content never paints unstyled.
 *  - Anything the theme already handles itself (cart drawer toggles, collection
 *    filters, infinite scroll) calls preventDefault first, and we bail on any
 *    click that has already been defaulted-prevented.
 *
 * Opt out of a link with `data-no-pjax` on the link or any ancestor.
 */
(() => {
  'use strict';

  if (window.themeNavigator) return;

  // The theme editor re-renders sections through its own channel; taking over
  // navigation there fights it and breaks section previews.
  if (window.Shopify && window.Shopify.designMode) return;

  if (!('pushState' in window.history) || !window.DOMParser || !window.fetch || !window.AbortController) {
    return;
  }

  const MAIN_SELECTOR = 'main[data-main]';
  const SECTION_SELECTOR = '.shopify-section';

  // First path segment (below the shop root) that must always be a real,
  // full-page navigation: Shopify owns the response, it may redirect off-site,
  // or the page depends on markup we deliberately don't swap.
  const EXCLUDED_SEGMENTS = new Set([
    'cart',
    'checkout',
    'account',
    'orders',
    'challenge',
    'password',
    'customer_identity',
    'customer_authentication',
    'admin',
    'apps',
    'tools',
    'services',
    'a',
    'wpm',
  ]);

  const FILE_EXTENSION = /\.(pdf|zip|csv|xml|txt|jpe?g|png|gif|webp|avif|svg|mp4|webm|mov|json|js|css)$/i;

  // How long a hover-prefetched response stays usable. Short on purpose: it is
  // only there to cover the gap between "pointer lands on the link" and "click",
  // never long enough to serve a stale price or a stale cart count.
  const PREFETCH_TTL = 5000;

  const root = (() => {
    const configured = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    return configured.endsWith('/') ? configured : `${configured}/`;
  })();

  const parser = new DOMParser();
  const loadedScripts = new Set();
  const loadedStyles = new Set();
  const prefetches = new Map();

  let controller = null;
  let inflightHref = null;
  let inflightPromise = null;
  let renderedPath = window.location.pathname;
  let renderedSearch = window.location.search;
  // Captured while this deferred script runs — before DOMContentLoaded, so
  // before theme.js has added any runtime class of its own.
  let serverBodyClasses = new Set((document.body.className || '').split(/\s+/).filter(Boolean));
  let progressBar = null;
  let progressTimer = null;

  /* ---------------------------------------------------------------- utils */

  function absolute(url) {
    try {
      return new URL(url, window.location.href).href;
    } catch (error) {
      return null;
    }
  }

  function registerExistingAssets() {
    document.querySelectorAll('script[src]').forEach((script) => {
      const href = absolute(script.getAttribute('src'));
      if (href) loadedScripts.add(href);
    });

    document.querySelectorAll('link[href]').forEach((link) => {
      const rel = (link.getAttribute('rel') || '').toLowerCase();
      if (rel !== 'stylesheet' && rel !== 'preload') return;
      const href = absolute(link.getAttribute('href'));
      if (href) loadedStyles.add(href);
    });
  }

  function isNavigableUrl(url) {
    if (!url || url.origin !== window.location.origin) return false;
    if (FILE_EXTENSION.test(url.pathname)) return false;

    const path = url.pathname.startsWith(root) ? url.pathname.slice(root.length) : url.pathname.replace(/^\//, '');
    const segment = path.split('/')[0].toLowerCase();

    if (EXCLUDED_SEGMENTS.has(segment)) return false;
    // /wpm@… and friends — Shopify infrastructure paths.
    if (segment.includes('@')) return false;

    return true;
  }

  function samePageHash(url) {
    return (
      url.hash &&
      url.pathname === window.location.pathname &&
      url.search === window.location.search
    );
  }

  /* ------------------------------------------------------------- progress */

  function startProgress() {
    if (!progressBar) {
      progressBar = document.createElement('div');
      progressBar.className = 'pjax-progress';
      progressBar.setAttribute('aria-hidden', 'true');
      document.body.appendChild(progressBar);
    }

    clearTimeout(progressTimer);
    progressBar.classList.remove('pjax-progress--done');
    // Only show up if the fetch is actually slow enough to be noticed —
    // a bar that flashes for 80ms is worse than no bar.
    progressTimer = setTimeout(() => {
      progressBar.classList.add('pjax-progress--active');
    }, 180);
  }

  function stopProgress() {
    clearTimeout(progressTimer);
    if (!progressBar) return;
    progressBar.classList.remove('pjax-progress--active');
    progressBar.classList.add('pjax-progress--done');
  }

  /* ----------------------------------------------------------- fetch layer */

  function fetchPage(url, signal) {
    return fetch(url, {
      signal,
      credentials: 'same-origin',
      headers: { Accept: 'text/html,application/xhtml+xml' },
    }).then((response) => {
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('text/html')) {
        throw new Error(`Unusable response for ${url}`);
      }
      return response.text().then((html) => ({ html, url: response.url || url }));
    });
  }

  function prefetch(url) {
    const key = url.href;
    const cached = prefetches.get(key);
    if (cached && Date.now() - cached.time < PREFETCH_TTL) return;

    const record = { time: Date.now(), promise: null };
    record.promise = fetchPage(key).catch(() => null);
    prefetches.set(key, record);

    // Keep the map tiny — this is a hover cache, not a page store.
    if (prefetches.size > 8) {
      prefetches.delete(prefetches.keys().next().value);
    }
  }

  function takePrefetch(href) {
    const record = prefetches.get(href);
    prefetches.delete(href);
    if (!record) return null;
    if (Date.now() - record.time > PREFETCH_TTL) return null;
    return record.promise;
  }

  /* --------------------------------------------------------------- assets */

  /**
   * Pull every stylesheet the incoming page references into <head> and wait for
   * it, so the swapped-in section is styled on its first paint. Section CSS in
   * this theme arrives as `<link rel="preload" as="style" onload="…">`; that
   * lazy pattern is right for a cold page load and wrong here, so we resolve it
   * to a plain stylesheet up front.
   */
  function adoptStylesheets(doc) {
    const links = Array.from(doc.querySelectorAll('link[href]')).filter((link) => {
      const rel = (link.getAttribute('rel') || '').toLowerCase();
      const as = (link.getAttribute('as') || '').toLowerCase();
      return rel === 'stylesheet' || (rel === 'preload' && as === 'style');
    });

    const pending = [];

    // Note: the <link> nodes are deliberately left in the parsed document. They
    // are inert duplicates once the stylesheet is in <head>, and removing them
    // would shift the child lists that reconcileChrome() morphs against — one
    // missing node there mis-pairs every sibling after it.
    links.forEach((link) => {
      const href = absolute(link.getAttribute('href'));
      if (!href || loadedStyles.has(href)) return;
      loadedStyles.add(href);

      const sheet = document.createElement('link');
      sheet.rel = 'stylesheet';
      sheet.href = href;
      document.head.appendChild(sheet);

      pending.push(
        new Promise((resolve) => {
          sheet.addEventListener('load', resolve, { once: true });
          sheet.addEventListener('error', resolve, { once: true });
          // Never let a hanging stylesheet hold the navigation hostage.
          setTimeout(resolve, 2000);
        })
      );
    });

    return Promise.all(pending);
  }

  /**
   * Scripts parsed by DOMParser are inert, so they have to be rebuilt. We lift
   * them out before the markup is inserted and put real ones back afterwards —
   * that way there is exactly one execution per script, whatever the browser
   * decides about the inert flag.
   */
  function extractScripts(container) {
    return Array.from(container.querySelectorAll('script')).map((script) => {
      const marker = document.createComment('pjax-script');
      script.replaceWith(marker);
      return { script, marker };
    });
  }

  function restoreScripts(entries) {
    entries.forEach(({ script, marker }) => {
      if (!marker.parentNode) return;

      const type = (script.getAttribute('type') || '').toLowerCase();
      const src = script.getAttribute('src');

      // application/ld+json and friends are data, not code: re-insert verbatim.
      const isData = type && type !== 'text/javascript' && type !== 'module' && !type.includes('javascript');

      if (src) {
        const href = absolute(src);
        if (!href || loadedScripts.has(href)) {
          marker.remove();
          return;
        }
        loadedScripts.add(href);
      }

      const fresh = document.createElement('script');
      Array.from(script.attributes).forEach((attr) => fresh.setAttribute(attr.name, attr.value));
      if (!src) fresh.textContent = script.textContent;
      // Dynamically inserted scripts are async by default; this keeps them in
      // document order, which some sections rely on.
      if (src && !isData) fresh.async = false;

      marker.replaceWith(fresh);
    });
  }

  /* ------------------------------------------------------------ head sync */

  function syncHead(doc) {
    document.title = doc.title;

    const pairs = [
      ['link[rel="canonical"]', 'href'],
      ['meta[name="description"]', 'content'],
      ['meta[property="og:title"]', 'content'],
      ['meta[property="og:description"]', 'content'],
      ['meta[property="og:url"]', 'content'],
      ['meta[property="og:image"]', 'content'],
      ['meta[property="og:type"]', 'content'],
      ['meta[name="twitter:title"]', 'content'],
      ['meta[name="twitter:description"]', 'content'],
    ];

    pairs.forEach(([selector, attribute]) => {
      const incoming = doc.querySelector(selector);
      const existing = document.head.querySelector(selector);
      if (incoming && existing) {
        existing.setAttribute(attribute, incoming.getAttribute(attribute) || '');
      } else if (incoming && !existing) {
        document.head.appendChild(document.importNode(incoming, true));
      } else if (!incoming && existing) {
        existing.remove();
      }
    });
  }

  /* ----------------------------------------------------------- chrome sync */

  /**
   * The header, announcement bar, rose row, mobile menu, popup and footer are
   * rendered on every page and are *nearly* identical between them — but never
   * exactly identical. Between two pages the header differs by which nav item
   * carries an --active class, by the --full-height the collection and search
   * templates set in its scoped <style>, by an extra JSON-LD block on the
   * homepage, and by a localization form token that is regenerated per request.
   *
   * So "replace it if the HTML changed" would rebuild the header on literally
   * every navigation, which is the flicker this whole file exists to avoid.
   * Instead we patch the existing tree in place: attributes, text and scoped
   * styles are synced onto the nodes that are already there, so every element
   * keeps its identity, its listeners and its custom-element state while the
   * active nav item and the scoped CSS still track the page you are on.
   */
  // Subtrees carrying client state the fetched markup can't know about.
  const LIVE_STATE = 'cart-element, [data-cart-drawer], template';

  /**
   * Deliberately conservative: this NEVER adds, removes, replaces or reorders a
   * node. It syncs classes, scoped <style> text and JSON-LD, and nothing else.
   *
   * A general-purpose morph looked right until the announcement bar lost its
   * slider arrows: the live chrome is no longer the server's markup. Flickity
   * wraps the slides in a generated viewport, so from that node down the two
   * trees describe different documents and patching one toward the other just
   * dismantles the slider. Everything that legitimately differs between two
   * pages of this theme is a class or a bit of text — the active nav item, the
   * rose row's --compact variant, the collection template's --full-height — so
   * sync that and leave the structure alone.
   *
   * Children are matched strictly by position. Where the trees have diverged
   * the walk stops descending, which degrades to "change nothing" instead of
   * "confidently sync the wrong node".
   */
  function syncNode(current, incoming) {
    const tag = current.tagName;

    if (tag === 'STYLE' || (tag === 'SCRIPT' && current.type === 'application/ld+json')) {
      if (current.textContent !== incoming.textContent) current.textContent = incoming.textContent;
      return;
    }

    if (typeof current.className === 'string' && typeof incoming.className === 'string') {
      // Same problem as <body>: some of these classes are the server's and some
      // were added at runtime by a component. Only ever retire a class this
      // element carried in the previous server render. On first sight the live
      // classes stand in for that render — safe here because the one part of
      // the chrome a component rewrites (Flickity's announcement slider) has
      // already diverged structurally, so the walk above never reaches it.
      const incomingClasses = new Set(incoming.className.split(/\s+/).filter(Boolean));
      const merged = new Set(current.className.split(/\s+/).filter(Boolean));

      const previous = current.pjaxServerClasses || new Set(merged);
      previous.forEach((name) => {
        if (!incomingClasses.has(name)) merged.delete(name);
      });
      incomingClasses.forEach((name) => merged.add(name));

      const next = Array.from(merged).join(' ');
      if (next !== current.className) current.className = next;
      current.pjaxServerClasses = incomingClasses;
    }

    const currentAria = current.getAttribute('aria-current');
    const incomingAria = incoming.getAttribute('aria-current');
    if (currentAria !== incomingAria) {
      if (incomingAria === null) current.removeAttribute('aria-current');
      else current.setAttribute('aria-current', incomingAria);
    }
  }

  function syncChrome(current, incoming) {
    if (current.matches(LIVE_STATE)) return;

    syncNode(current, incoming);

    const mine = current.children;
    const theirs = incoming.children;
    const shared = Math.min(mine.length, theirs.length);

    for (let index = 0; index < shared; index += 1) {
      const child = mine[index];
      const match = theirs[index];
      // First mismatch means a component has restructured this subtree
      // (Flickity's viewport wrapper). Stop rather than guess.
      if (child.tagName !== match.tagName || child.id !== match.id) break;
      syncChrome(child, match);
    }
  }

  // A chrome section whose *shape* is template-dependent opts in with
  // data-pjax-reload: patching classes can't add or drop an element, so those
  // sections are rebuilt instead. The rose row does this — its compact variant
  // off the homepage omits the mint tab entirely.
  const RELOAD_MARKER = '[data-pjax-reload]';

  function skeleton(root) {
    const tags = [];
    (function walk(node) {
      for (let index = 0; index < node.children.length; index += 1) {
        tags.push(node.children[index].tagName);
        walk(node.children[index]);
      }
    })(root);
    return tags.join(',');
  }

  function reconcileChrome(doc) {
    const outsideMain = (root) =>
      Array.from(root.querySelectorAll(SECTION_SELECTOR)).filter((section) => !section.closest(MAIN_SELECTOR));

    const incomingById = new Map(outsideMain(doc).map((section) => [section.id, section]));

    outsideMain(document).forEach((current) => {
      const incoming = incomingById.get(current.id);
      // No counterpart on the new page: leave the existing chrome alone rather
      // than tear down a header the incoming markup simply didn't include.
      if (!incoming) return;
      if (current.outerHTML === incoming.outerHTML) return;

      const optsIntoReload = current.querySelector(RELOAD_MARKER) || incoming.querySelector(RELOAD_MARKER);
      if (optsIntoReload && skeleton(current) !== skeleton(incoming)) {
        const replacement = document.importNode(incoming, true);
        const scripts = extractScripts(replacement);
        current.replaceWith(replacement);
        restoreScripts(scripts);
        notifySectionLoad(replacement);
        return;
      }

      syncChrome(current, incoming);
    });
  }

  /* ------------------------------------------------------------ section IO */

  function notifySectionLoad(section) {
    const id = section.id ? section.id.replace(/^shopify-section-/, '') : '';
    section.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: id },
      })
    );
  }

  function notifySectionUnload(section) {
    const id = section.id ? section.id.replace(/^shopify-section-/, '') : '';
    section.dispatchEvent(
      new CustomEvent('shopify:section:unload', {
        bubbles: true,
        detail: { sectionId: id },
      })
    );
  }

  /* ------------------------------------------------------------ overlays  */

  // Navigating from inside a drawer or the search popdown would leave the
  // overlay, its focus trap and the body scroll-lock behind — a full page load
  // used to clear all three for free. Close them through the theme's own close
  // buttons rather than by faking its events (`theme:*:close` are notifications
  // the components *emit*, so dispatching them by hand desynchronises the
  // header instead of closing anything). The open marker is `is-open`, which
  // both components toggle synchronously in BOTH directions. It must not be
  // `:not(.cv-h)`: the cart drawer sheds `cv-h` the first time it opens and
  // theme.js never restores it on close, so after one open every navigation
  // matched the closed drawer and the "close" click *toggled it open*.
  function closeOverlays() {
    const controls = [
      'drawer-element.is-open .drawer__close-button',
      'cart-element[data-cart-drawer].is-open [data-cart-drawer-toggle]',
    ];

    controls.forEach((selector) => {
      const button = document.querySelector(selector);
      if (button) button.click();
    });

    if (document.body.classList.contains('search-opened')) {
      const close = document.querySelector('.search-popdown [data-popdown-close]');
      if (close) close.click();
    }
  }

  /* --------------------------------------------------------------- render */

  /**
   * <body> carries two kinds of class: the ones Liquid renders (template-*,
   * palette--*, customer-logged-in) and the ones theme.js adds at runtime
   * (hide-header, search-opened, cart-opened…). Assigning className wholesale
   * would take the fetched page's server classes *and* silently drop every
   * runtime one, so the sticky header would forget it was hidden. Swap only the
   * classes the server owns, tracked from render to render.
   */
  function syncBodyClasses(doc) {
    const incoming = new Set((doc.body.className || '').split(/\s+/).filter(Boolean));
    const merged = new Set((document.body.className || '').split(/\s+/).filter(Boolean));

    serverBodyClasses.forEach((name) => {
      if (!incoming.has(name)) merged.delete(name);
    });
    incoming.forEach((name) => merged.add(name));

    document.body.className = Array.from(merged).join(' ');
    serverBodyClasses = incoming;
  }

  let morphTimer = null;

  function applyDocument(doc, url, scrollTo) {
    // Arms the header's cross-page morph (theme.css html.pjax-morph rules):
    // crossing between the homepage's three-layer header and the inner
    // pages' two-layer one animates the tab and card instead of snapping.
    // The window outlives this function by the transition's length, then
    // closes so the scroll-linked fold goes back to tracking 1:1.
    document.documentElement.classList.add('pjax-morph');
    clearTimeout(morphTimer);
    morphTimer = setTimeout(() => {
      document.documentElement.classList.remove('pjax-morph');
    }, 600);

    syncHead(doc);

    syncBodyClasses(doc);
    if (doc.body.id) document.body.id = doc.body.id;

    reconcileChrome(doc);

    const currentMain = document.querySelector(MAIN_SELECTOR);
    const incomingMain = doc.querySelector(MAIN_SELECTOR);
    if (!currentMain || !incomingMain) return false;

    currentMain.querySelectorAll(SECTION_SELECTOR).forEach(notifySectionUnload);

    const replacement = document.importNode(incomingMain, true);
    const scripts = extractScripts(replacement);

    currentMain.replaceWith(replacement);
    restoreScripts(scripts);

    // Scroll before the sections boot, so anything that measures viewport
    // position (sticky bars, in-view animations) reads the final position.
    if (scrollTo === 'top') {
      window.scrollTo(0, 0);
    } else if (typeof scrollTo === 'number') {
      window.scrollTo(0, scrollTo);
    } else if (typeof scrollTo === 'string') {
      const target = document.getElementById(scrollTo) || document.querySelector(`[name="${CSS.escape(scrollTo)}"]`);
      if (target) target.scrollIntoView();
      else window.scrollTo(0, 0);
    }

    if (window.theme && typeof window.theme.setVars === 'function') window.theme.setVars();

    replacement.querySelectorAll(SECTION_SELECTOR).forEach(notifySectionLoad);

    window.dispatchEvent(new Event('resize'));

    renderedPath = url.pathname;
    renderedSearch = url.search;

    return true;
  }

  function afterRender(url) {
    // Move focus to the new content so keyboard and screen-reader users are not
    // left on a link that no longer exists.
    const main = document.querySelector(MAIN_SELECTOR);
    if (main) {
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
      main.removeAttribute('tabindex');
    }

    document.dispatchEvent(
      new CustomEvent('theme:navigation:end', { bubbles: true, detail: { url: url.href } })
    );

    // Best effort page-view for Shopify analytics / web pixels. A soft
    // navigation is invisible to them otherwise.
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('page_viewed', {});
      }
    } catch (error) {
      /* analytics must never break navigation */
    }
  }

  /* ----------------------------------------------------------------- visit */

  function hardNavigate(url) {
    window.location.href = typeof url === 'string' ? url : url.href;
  }

  function visit(url, options = {}) {
    const { push = true, scrollTo = 'top' } = options;

    // An impatient repeat click on the same link joins the navigation already
    // in flight. Aborting and refetching here restarts the clock from zero, so
    // on a slow connection a visitor who clicks again every second or two
    // never gets anywhere — the page only changes once they stop clicking.
    if (controller && !controller.signal.aborted && inflightHref === url.href && inflightPromise) {
      return inflightPromise;
    }

    if (controller) controller.abort();
    controller = new AbortController();
    const signal = controller.signal;
    inflightHref = url.href;

    closeOverlays();
    startProgress();
    document.documentElement.classList.add('is-navigating');
    document.dispatchEvent(
      new CustomEvent('theme:navigation:start', { bubbles: true, detail: { url: url.href } })
    );

    const cached = takePrefetch(url.href);
    const request = cached
      ? cached.then((result) => result || fetchPage(url.href, signal))
      : fetchPage(url.href, signal);

    inflightPromise = request
      .then((result) => {
        if (signal.aborted) return null;

        const finalUrl = new URL(result.url, window.location.href);

        // A redirect can land somewhere we deliberately never soft-render
        // (checkout, account). Hand it back to the browser.
        if (!isNavigableUrl(finalUrl)) {
          hardNavigate(finalUrl);
          return null;
        }

        const doc = parser.parseFromString(result.html, 'text/html');
        if (!doc.querySelector(MAIN_SELECTOR)) {
          hardNavigate(finalUrl);
          return null;
        }

        return adoptStylesheets(doc).then(() => {
          if (signal.aborted) return null;

          if (push) {
            // Stamp the entry we are leaving with the reader's position before
            // pushing the new one, so going back returns them to where they
            // were in a collection rather than to the top of it. location.href
            // is still the outgoing page here — this is the only moment the
            // scroll offset and its history entry line up.
            window.history.replaceState(
              Object.assign({}, window.history.state, { pjax: true, scrollY: window.scrollY }),
              '',
              window.location.href
            );
            window.history.pushState({ pjax: true, url: finalUrl.href }, '', finalUrl.href);
          } else {
            window.history.replaceState(
              Object.assign({}, window.history.state, { pjax: true, url: finalUrl.href }),
              '',
              finalUrl.href
            );
          }

          const target = finalUrl.hash ? finalUrl.hash.slice(1) : scrollTo;

          // Swapped in directly, with no view-transition crossfade: the pages
          // are pale enough that the default fade read as a flash of white.
          let applied = false;
          const render = () => {
            applied = applyDocument(doc, finalUrl, target);
          };

          render();
          if (applied) afterRender(finalUrl);
          else hardNavigate(finalUrl);
          return null;
        });
      })
      .catch((error) => {
        if (signal.aborted || (error && error.name === 'AbortError')) return;
        // Anything unexpected: fall back to a real navigation rather than
        // leaving the visitor stranded on the old page.
        hardNavigate(url);
      })
      .finally(() => {
        // A superseded visit settles after its replacement has started; its
        // cleanup must not tear down the progress state the live visit owns.
        if (controller && controller.signal !== signal) return;
        stopProgress();
        document.documentElement.classList.remove('is-navigating');
        inflightHref = null;
        inflightPromise = null;
      });

    return inflightPromise;
  }

  /* -------------------------------------------------------------- handlers */

  function linkFor(event) {
    if (event.defaultPrevented) return null;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return null;

    if (link.hasAttribute('download')) return null;
    if (link.hasAttribute('data-cart-drawer-toggle')) return null;
    if (link.closest('[data-no-pjax]')) return null;

    const target = link.getAttribute('target');
    if (target && target !== '_self') return null;

    const rel = (link.getAttribute('rel') || '').toLowerCase();
    if (rel.split(/\s+/).includes('external')) return null;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return null;

    const url = absolute(href);
    return url ? new URL(url) : null;
  }

  document.addEventListener('click', (event) => {
    const url = linkFor(event);
    if (!url || !isNavigableUrl(url)) return;
    if (samePageHash(url)) return;

    event.preventDefault();
    visit(url);
  });

  // GET forms (site search, and anything else that navigates by query string).
  // POST forms — add-to-cart, newsletter, localisation, customer forms — are
  // left entirely alone.
  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if ((form.method || 'get').toLowerCase() !== 'get') return;
    if (form.closest('[data-no-pjax]')) return;
    if (form.hasAttribute('data-no-pjax')) return;

    const action = absolute(form.getAttribute('action') || window.location.pathname);
    if (!action) return;

    const url = new URL(action);
    if (!isNavigableUrl(url)) return;

    const params = new URLSearchParams(new FormData(form));
    url.search = params.toString();

    event.preventDefault();
    visit(url);
  });

  // Warm the next page while the pointer is still travelling toward the link.
  // Delayed a little so sweeping the cursor across a grid of products doesn't
  // fire a request per card.
  let prefetchTimer = null;

  function candidateUrl(target) {
    const link = target instanceof Element ? target.closest('a[href]') : null;
    if (!link || link.hasAttribute('data-no-pjax') || link.closest('[data-no-pjax]')) return null;
    if (link.hasAttribute('data-cart-drawer-toggle')) return null;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return null;

    const url = absolute(href);
    if (!url) return null;

    const parsed = new URL(url);
    if (!isNavigableUrl(parsed) || parsed.href === window.location.href) return null;

    return parsed;
  }

  function queuePrefetch(event) {
    const url = candidateUrl(event.target);
    clearTimeout(prefetchTimer);
    if (!url) return;
    prefetchTimer = setTimeout(() => prefetch(url), 70);
  }

  // A page fetched on hover carries the cart state as it was at fetch time, and
  // the header morph would then paint a stale cart count. Throw the cache away
  // whenever the cart changes rather than reason about the race.
  ['theme:cart:added', 'theme:cart:update', 'theme:cart:refresh'].forEach((name) => {
    document.addEventListener(name, () => prefetches.clear());
  });

  document.addEventListener('pointerover', queuePrefetch);
  document.addEventListener('focusin', queuePrefetch);
  document.addEventListener(
    'touchstart',
    (event) => {
      const url = candidateUrl(event.target);
      if (url) prefetch(url);
    },
    { passive: true }
  );

  window.addEventListener('popstate', () => {
    const url = new URL(window.location.href);
    if (!isNavigableUrl(url)) return;

    const pathChanged = url.pathname !== renderedPath;
    const searchChanged = url.search !== renderedSearch;

    if (!pathChanged && !searchChanged) return;

    // Collection filtering owns its own history entries — filters.js pushes a
    // query string and re-renders the grid itself, so stay out of same-path
    // query changes whenever that component is on the page.
    if (!pathChanged && document.querySelector('collection-filters')) return;

    // product-info replaceState()s on variant changes and drops our scrollY,
    // hence the fallback rather than a bare read.
    const state = window.history.state;
    const restore = state && typeof state.scrollY === 'number' ? state.scrollY : 'top';

    visit(url, { push: false, scrollTo: restore });
  });

  registerExistingAssets();

  // We restore scroll ourselves, positioned against the swapped-in content.
  // Left on 'auto' the browser scrolls the *old* DOM the moment popstate fires,
  // which lands somewhere arbitrary once the new page renders.
  if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

  window.history.replaceState(
    Object.assign({}, window.history.state, { pjax: true, url: window.location.href }),
    '',
    window.location.href
  );

  window.themeNavigator = { visit, prefetch };
})();
